import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, DollarSign, MapPin, CheckCircle, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useConference } from '@/context/ConferenceContext';
import { getWorkshopsByConference, getErrorMessage } from '@/services/api';
import { toast } from 'sonner';

const WorkshopsPage = () => {
  const { shortName, importantDetails, loading: confLoading, siteConfig } = useConference();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkshops = async () => {
      if (confLoading) return;

      setLoading(true);
      try {
        const data = await getWorkshopsByConference(shortName);
        setWorkshops(data || []);
      } catch (error) {
        console.error('Failed to fetch workshops:', error);
        toast.error(getErrorMessage(error));
        setWorkshops([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [shortName, confLoading]);

  if (loading || confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading workshops...</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <Helmet>
        <title>Workshops - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Pre-conference and post-conference workshops at CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Workshops</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Hands-on learning sessions led by industry experts and software specialists
              </p>
            </motion.div>
          </div>
        </section>

        {/* Workshops List */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {workshops.map((workshop, index) => {
                const enrollmentPercent = workshop.capacity > 0 ? (workshop.enrolled / workshop.capacity) * 100 : 0;
                const isFull = workshop.enrolled >= workshop.capacity;

                return (
                  <motion.div
                    key={workshop.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="card-hover bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden"
                    data-testid={`workshop-${workshop.id}`}
                  >
                    <div className="p-6 md:p-8">
                      {/* Header */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                        <div className="flex-1">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">{workshop.title}</h2>
                          <p className="text-gray-700 font-medium mb-1">Instructor: {workshop.instructor}</p>
                          <div className="flex flex-wrap gap-3 mt-3">
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <Calendar size={16} />
                              <span>{workshop.date ? new Date(workshop.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'TBD'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <Clock size={16} />
                              <span>{workshop.time}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <MapPin size={16} />
                              <span>{workshop.room}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-3">
                          <div className="text-3xl font-bold text-sky-500">${workshop.fee}</div>
                          {isFull ? (
                            <Badge variant="destructive" className="bg-red-500">FULL</Badge>
                          ) : (
                            <Badge variant="default" className="bg-green-500">Available</Badge>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 leading-relaxed mb-6">{workshop.description}</p>

                      {/* Topics */}
                      {workshop.topics && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">Topics Covered:</h3>
                          <div className="text-gray-700">
                            {workshop.topics}
                          </div>
                        </div>
                      )}

                      {/* Prerequisites */}
                      {workshop.prerequisites && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Prerequisites:</h3>
                          <p className="text-gray-600">{workshop.prerequisites}</p>
                        </div>
                      )}

                      {/* Capacity */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Users size={18} />
                            <span className="font-medium">Enrollment</span>
                          </div>
                          <span className="text-sm text-gray-600">
                            {workshop.enrolled} / {workshop.capacity} participants
                          </span>
                        </div>
                        <Progress value={enrollmentPercent} className="h-2" />
                      </div>

                      {/* Register Button */}
                      <div className="flex justify-end">
                        <Link to={`/register?workshop=${workshop.id}`}>
                          <Button
                            disabled={isFull}
                            data-testid={`register-workshop-${workshop.id}`}
                            className="bg-sky-500 hover:bg-sky-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {isFull ? 'Workshop Full' : 'Register for Workshop'}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Workshop Information</h2>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Registration</h3>
                <p className="text-gray-600">
                  Workshop registration is separate from conference registration. You can add workshops during the conference registration process or register for them separately. Early registration is recommended as workshops have limited capacity.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Certificates</h3>
                <p className="text-gray-600">
                  All workshop participants will receive a certificate of completion and Professional Development Hours (PDH) credits. Certificates will be distributed at the end of each workshop.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Materials</h3>
                <p className="text-gray-600">
                  Workshop materials, including slides, sample files, and resources, will be provided digitally to all registered participants. Please bring your own laptop for hands-on workshops.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancellation Policy</h3>
                <p className="text-gray-600">
                  Workshop registrations can be cancelled up to 7 days before the workshop date for a full refund. No refunds will be issued for cancellations within 7 days of the workshop.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WorkshopsPage;
